<!-- SubthreadTimeline.vue -->
<template>
  <div class="subthread-timeline">
    <!-- Detailed Subthread View Modal -->
    <div v-if="viewingSubthread" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="fixed inset-0 bg-gray-500/75" @click="closeSubthreadView"></div>

        <div class="bg-white rounded-lg max-w-3xl w-full relative z-10 max-h-[90vh] overflow-hidden flex flex-col">
          <!-- Header -->
          <div class="px-6 py-4 border-b">
            <div class="flex items-center justify-between">
              <h3 class="text-xs font-semibold">Interaction Details</h3>
              <button
                @click="closeSubthreadView"
                class="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon :icon="['fas', 'times']" class="text-xs" />
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto px-6 py-4">
            <!-- Timestamp and metadata -->
            <div class="flex items-center gap-2 mb-4 flex-wrap">
              <span class="text-xs font-medium text-gray-900">
                {{ formatDate(viewingSubthread.created_at) }}
              </span>
              <span
                v-if="viewingSubthread.metadata?.observation_type"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {{ formatObservationType(viewingSubthread.metadata.observation_type) }}
              </span>
              <span
                v-if="viewingSubthread.metadata?.created_from_suggestion"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
              >
                <FontAwesomeIcon :icon="['fas', 'robot']" class="mr-1" />
                AI-Suggested
              </span>
            </div>

            <!-- Observation -->
            <div class="mb-6">
              <h4 class="text-xs font-semibold text-gray-700 mb-2">Observation</h4>
              <p class="text-xs text-gray-900 whitespace-pre-wrap leading-relaxed">
                {{ getObservationText(viewingSubthread) }}
              </p>
            </div>

            <!-- Rationale -->
            <div v-if="viewingSubthread.metadata?.rationale" class="mb-6">
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 class="text-xs font-semibold text-blue-900 mb-2 flex items-center">
                  <FontAwesomeIcon :icon="['fas', 'lightbulb']" class="mr-2" />
                  Rationale
                </h4>
                <p class="text-xs text-blue-800">{{ viewingSubthread.metadata.rationale }}</p>
              </div>
            </div>

            <!-- Evidence -->
            <div v-if="viewingSubthread.metadata?.evidence" class="mb-6">
              <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 class="text-xs font-semibold text-purple-900 mb-3 flex items-center">
                  <FontAwesomeIcon :icon="['fas', 'shield-check']" class="mr-2" />
                  Supporting Evidence
                </h4>

                <div class="space-y-3">
                  <div>
                    <p class="text-xs font-medium text-purple-700 mb-1">Evidence Type</p>
                    <p class="text-xs text-purple-800">{{ viewingSubthread.metadata.evidence.evidence_source_type }}</p>
                  </div>

                  <!-- Session Evidence -->
                  <div v-if="viewingSubthread.metadata.evidence.session_evidence?.length">
                    <p class="text-xs font-medium text-purple-700 mb-2">Session Evidence</p>
                    <div class="space-y-2">
                      <div
                        v-for="(ev, idx) in viewingSubthread.metadata.evidence.session_evidence"
                        :key="idx"
                        class="bg-white rounded p-2 text-xs"
                      >
                        <p class="text-purple-900 font-medium">{{ ev.source_type }}</p>
                        <p class="text-gray-700 italic">"{{ ev.exact_quote }}"</p>
                        <p class="text-gray-600 text-xs mt-1">{{ ev.how_it_supports || ev.relevance }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Clinical Summary Evidence -->
                  <div v-if="viewingSubthread.metadata.evidence.clinical_summary_evidence?.length">
                    <p class="text-xs font-medium text-purple-700 mb-2">Clinical Summary Evidence</p>
                    <div class="space-y-2">
                      <div
                        v-for="(ev, idx) in viewingSubthread.metadata.evidence.clinical_summary_evidence"
                        :key="idx"
                        class="bg-white rounded p-2 text-xs"
                      >
                        <p class="text-purple-900 font-medium">{{ ev.summary_type.toUpperCase() }}: {{ ev.specific_finding }}</p>
                        <p class="text-gray-700 text-xs mt-1">{{ ev.clinical_significance || ev.why_needs_module }}</p>
                      </div>
                    </div>
                  </div>

                  <div v-if="viewingSubthread.metadata.confidence">
                    <p class="text-xs font-medium text-purple-700 mb-1">Confidence</p>
                    <p class="text-xs text-purple-800">{{ Math.round(viewingSubthread.metadata.confidence * 100) }}%</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Clinical Significance -->
            <div v-if="viewingSubthread.metadata?.clinical_significance" class="mb-6">
              <h4 class="text-xs font-semibold text-gray-700 mb-2">Clinical Significance</h4>
              <p class="text-xs text-gray-700">{{ viewingSubthread.metadata.clinical_significance }}</p>
            </div>

            <!-- Additional Metadata -->
            <div v-if="hasAdditionalMetadata(viewingSubthread)" class="mb-6">
              <h4 class="text-xs font-semibold text-gray-700 mb-2">Additional Information</h4>
              <div class="bg-gray-50 rounded-lg p-3 space-y-2 text-xs">
                <div v-for="(value, key) in getAdditionalMetadata(viewingSubthread)" :key="key" class="flex">
                  <span class="text-gray-600 font-medium w-1/3">{{ formatMetadataKey(key) }}:</span>
                  <span class="text-gray-900">{{ formatMetadataValue(value) }}</span>
                </div>
              </div>
            </div>

            <!-- Work Session Link -->
            <div v-if="viewingSubthread.metadata?.work_id" class="pt-4 border-t">
              <a
                :href="`/work/clinicalengagement/${viewingSubthread.metadata.work_id}`"
                target="_blank"
                class="text-xs text-primary-600 hover:text-primary-700 font-medium inline-flex items-center"
              >
                <FontAwesomeIcon :icon="['fas', 'external-link']" class="mr-2" />
                View Related Work Session
              </a>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 border-t flex justify-between">
            <div class="text-xs text-gray-500">
              Created: {{ formatDate(viewingSubthread.created_at) }}
            </div>
            <button
              @click="closeSubthreadView"
              class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!subthreads || subthreads.length === 0" class="text-center py-6 text-gray-500">
      <FontAwesomeIcon :icon="['fas', 'timeline']" class="text-xs mb-2 opacity-50" />
      <p class="text-xs">No patient interactions recorded yet</p>
    </div>

    <!-- Timeline -->
    <div v-else class="relative">
      <!-- Timeline entries -->
      <div
        v-for="(subthread, index) in displayedSubthreads"
        :key="subthread.id"
        class="timeline-entry relative pb-6 last:pb-0"
        :class="{ 'cursor-pointer hover:bg-gray-50 rounded-lg transition-colors': !disableClick }"
        @click="handleEntryClick(subthread)"
      >
        <!-- Timeline line (not for last item) -->
        <div
          v-if="index < displayedSubthreads.length - 1"
          class="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200"
        ></div>

        <!-- Entry content -->
        <div class="relative flex items-start space-x-3 pl-2">
          <!-- Timeline dot -->
          <div class="shrink-0 relative z-10">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center"
              :class="getTimelineDotClass(subthread)"
            >
              <FontAwesomeIcon
                :icon="getObservationIcon(subthread)"
                class="text-xs"
              />
            </div>
          </div>

          <!-- Entry details -->
          <div class="flex-1 min-w-0 pt-1">
            <!-- Timestamp and metadata badges -->
            <div class="flex items-center gap-2 mb-1 flex-wrap">
              <span class="text-xs font-medium text-gray-900">
                {{ formatDate(subthread.created_at) }}
              </span>
              <span
                v-if="subthread.metadata?.observation_type"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {{ formatObservationType(subthread.metadata.observation_type) }}
              </span>
              <span
                v-if="subthread.metadata?.created_from_suggestion"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
              >
                <FontAwesomeIcon :icon="['fas', 'robot']" class="mr-1" />
                AI-Suggested
              </span>
            </div>

            <!-- Observation text -->
            <div class="text-xs text-gray-900 leading-relaxed">
              <p v-if="mode === 'compact'" class="line-clamp-2">
                {{ getObservationText(subthread) }}
              </p>
              <p v-else class="whitespace-pre-wrap">
                {{ getObservationText(subthread) }}
              </p>
            </div>

            <!-- Additional details for detailed mode -->
            <div v-if="mode === 'detailed'" class="mt-3 space-y-2">
              <!-- Rationale -->
              <div v-if="subthread.metadata?.rationale" class="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p class="text-xs font-semibold text-blue-900 mb-1">
                  <FontAwesomeIcon :icon="['fas', 'lightbulb']" class="mr-1" />
                  Rationale
                </p>
                <p class="text-xs text-blue-800">{{ subthread.metadata.rationale }}</p>
              </div>

              <!-- Evidence (if from AI suggestion) -->
              <div v-if="subthread.metadata?.evidence" class="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div class="flex items-center justify-between mb-2">
                  <p class="text-xs font-semibold text-purple-900">
                    <FontAwesomeIcon :icon="['fas', 'shield-check']" class="mr-1" />
                    Supporting Evidence
                  </p>
                  <button
                    @click.stop="showEvidence(subthread)"
                    class="text-xs text-purple-600 hover:text-purple-700 font-medium"
                  >
                    View Details
                  </button>
                </div>
                <p class="text-xs text-purple-700">
                  Confidence: {{ Math.round((subthread.metadata.confidence || 0) * 100) }}%
                </p>
              </div>

              <!-- Clinical Significance -->
              <div v-if="subthread.metadata?.clinical_significance" class="text-xs text-gray-700">
                <span class="font-medium">Clinical Significance:</span>
                {{ subthread.metadata.clinical_significance }}
              </div>

              <!-- Work Session Link -->
              <div v-if="subthread.metadata?.work_id" class="pt-2 border-t">
                <a
                  :href="`/work/clinicalengagement/${subthread.metadata.work_id}`"
                  @click.stop
                  class="text-xs text-primary-600 hover:text-primary-700 font-medium inline-flex items-center"
                >
                  <FontAwesomeIcon :icon="['fas', 'external-link']" class="mr-1" />
                  View Work Session
                </a>
              </div>
            </div>

            <!-- Compact mode - show "Click for details" hint -->
            <div v-if="mode === 'compact' && !disableClick" class="mt-2">
              <span class="text-xs text-gray-500 italic">Click for details...</span>
            </div>
          </div>

          <!-- Delete button (if enabled) -->
          <button
            v-if="showDelete"
            @click.stop="$emit('delete', subthread)"
            class="shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete interaction"
          >
            <FontAwesomeIcon :icon="['fas', 'trash']" class="text-xs" />
          </button>
        </div>
      </div>

      <!-- Show more button (compact mode only) -->
      <div v-if="mode === 'compact' && hasMore" class="pt-4 border-t">
        <button
          @click="$emit('show-all')"
          class="w-full text-xs text-primary-600 hover:text-primary-700 font-medium"
        >
          View all {{ subthreads.length }} interactions
          <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="ml-1" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'SubthreadTimeline',

  components: {
    FontAwesomeIcon
  },

  props: {
    subthreads: {
      type: Array,
      default: () => []
    },
    mode: {
      type: String,
      default: 'compact', // 'compact' or 'detailed'
      validator: (value) => ['compact', 'detailed'].includes(value)
    },
    maxCompactItems: {
      type: Number,
      default: 5
    },
    showDelete: {
      type: Boolean,
      default: false
    },
    disableClick: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      viewingSubthread: null
    };
  },

  emits: ['delete', 'show-all', 'show-evidence'],

  computed: {
    displayedSubthreads() {
      if (this.mode === 'detailed') {
        return this.subthreads;
      }
      // In compact mode, show only the most recent N items
      return this.subthreads.slice(0, this.maxCompactItems);
    },

    hasMore() {
      return this.subthreads.length > this.maxCompactItems;
    }
  },

  methods: {
    handleEntryClick(subthread) {
      if (!this.disableClick) {
        if (this.mode === 'compact') {
          // In compact mode, open the detailed view modal
          this.viewingSubthread = subthread;
        }
      }
    },

    closeSubthreadView() {
      this.viewingSubthread = null;
    },

    hasAdditionalMetadata(subthread) {
      if (!subthread.metadata) return false;

      // Exclude known fields that are already displayed
      const excludeKeys = [
        'observation', 'rationale', 'evidence', 'confidence',
        'created_from_suggestion', 'suggestion_id', 'work_id',
        'observation_type', 'clinical_significance'
      ];

      const additionalKeys = Object.keys(subthread.metadata).filter(
        key => !excludeKeys.includes(key)
      );

      return additionalKeys.length > 0;
    },

    getAdditionalMetadata(subthread) {
      if (!subthread.metadata) return {};

      const excludeKeys = [
        'observation', 'rationale', 'evidence', 'confidence',
        'created_from_suggestion', 'suggestion_id', 'work_id',
        'observation_type', 'clinical_significance'
      ];

      const filtered = {};
      for (const [key, value] of Object.entries(subthread.metadata)) {
        if (!excludeKeys.includes(key)) {
          filtered[key] = value;
        }
      }

      return filtered;
    },

    formatMetadataKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    formatMetadataValue(value) {
      if (value === null || value === undefined) return 'N/A';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'object') return JSON.stringify(value, null, 2);
      if (Array.isArray(value)) return value.join(', ');
      return value.toString();
    },

    getObservationText(subthread) {
      // Try metadata.observation first (new format), then fall back to task
      return subthread.metadata?.observation || subthread.task || 'No observation recorded';
    },

    getObservationIcon(subthread) {
      const observationType = subthread.metadata?.observation_type;

      const iconMap = {
        'clinical_note': ['fas', 'notes-medical'],
        'new_diagnosis': ['fas', 'stethoscope'],
        'medication_change': ['fas', 'prescription-bottle'],
        'abnormal_result': ['fas', 'flask'],
        'disease_progression': ['fas', 'chart-line'],
        'complication_identified': ['fas', 'triangle-exclamation']
      };

      return iconMap[observationType] || ['fas', 'circle-check'];
    },

    getTimelineDotClass(subthread) {
      const observationType = subthread.metadata?.observation_type;

      const classMap = {
        'clinical_note': 'bg-blue-100 text-blue-600',
        'new_diagnosis': 'bg-red-100 text-red-600',
        'medication_change': 'bg-purple-100 text-purple-600',
        'abnormal_result': 'bg-yellow-100 text-yellow-600',
        'disease_progression': 'bg-orange-100 text-orange-600',
        'complication_identified': 'bg-red-100 text-red-600'
      };

      return classMap[observationType] || 'bg-green-100 text-green-600';
    },

    formatObservationType(type) {
      if (!type) return '';
      return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown date';

      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now - date;
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      // If today
      if (diffInDays === 0) {
        return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
      }
      // If yesterday
      if (diffInDays === 1) {
        return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
      }
      // If within last week
      if (diffInDays < 7) {
        return `${diffInDays} days ago`;
      }
      // Otherwise show full date
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    },

    showEvidence(subthread) {
      this.$emit('show-evidence', subthread);
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.timeline-entry {
  position: relative;
}
</style>
